import * as eventsModel from './models/events';
import * as panelModel from './models/panel';
import * as actionModel from './models/action';
import addTargetEventListener from '../events/addTargetEventListener';
import { selectors } from './elements/constants';
import check_email from './ajax/checkEmail';
import check_tutor_details, {
  checkTutorPhoneNumber as check_tutor_phone
} from './ajax/checkTutorDetails';
import new_contact from './ajax/createContact';
import new_tutor from './ajax/createTutor';
import new_user from './ajax/createUser';
import update_contact from './ajax/updateContact';
import new_callback from './ajax/createCallback';
import getForm from './elements/getForm';
import getStep from './elements/getStep';

const steps = {
  check_email,
  check_tutor_details,
  check_tutor_phone,
  new_contact,
  new_tutor,
  new_user,
  update_contact,
  new_callback
};

/**
 * Calls the appropriate action function, depending on the step's `action` data attribute.
 * @param {Element} step - The current step
 */
async function performAction(step) {
  if(step.dataset.action in steps) {
    return steps[step.dataset.action](step);
  }
}

/**
 * Handles `formWizard.hideStep` events called on submit forms.
 * @param {Event} event - The current event being handled
 */
function handleHideStep(event) {
  const step = getStep(event.target);
  const { direction } = event.detail;

  // mark this step as 'complete' so that it can be queried for fields
  panelModel.updatePanelClasses(step, { active: false, complete: direction !== 'backwards' });
}

/**
 * Dispatches a navigation event forwards.
 * @param {Element} fromAction - The current step
 */
function gotoNextStep(fromAction) {
  eventsModel.dispatchNavigateEvent(fromAction, { direction: 'forwards' });
}

/**
 * Handles an unexpected error during form submission, logging it to NewRelic and
 * rendering it to the screen.
 * @param {Element} step - The current step throwing the error
 * @param {Error} error - The error raised during submission
 */
function handleFailure(step, error) {
  if(window.newrelic) {
    window.newrelic.noticeError(error, { 'customAttributes': step.dataset });
  }

  // get last 'visible' panel, and tell it to render a custom error
  const panel = panelModel.getActivePanel(getForm(step));
  if(panel) { eventsModel.dispatchCustomErrorEvent(panel, error); }
}

/**
 * Handles `formWizard.showStep` events called on submit forms, i.e. process any
 * conditions and go to the next step.
 * @param {Event} event - The current event being handled
 */
async function handleShowStep(event) {
  const step = getStep(event.target);
  if (!step) { return; }

  // mark this step as 'active' so that it can be queried for fields
  panelModel.updatePanelClasses(step, { active: true, complete: false });

  if(!actionModel.conditionsMet(step)) {
    return gotoNextStep(step);
  }

  try {
    await performAction(step);
    gotoNextStep(step);
  } catch(error) {
    handleFailure(step, error);
  }
}

addTargetEventListener(eventsModel.events.showStep, selectors.actionSubmitForm, handleShowStep);
addTargetEventListener(eventsModel.events.hideStep, selectors.actionSubmitForm, handleHideStep);
